import React, { useState, useRef } from 'react'
import { Input, Button, ConfigProvider } from 'antd'
import classnames from 'classnames'
import SparkleIndicator from '../SparkleIndicator'
import { ACTION_TYPES, RECIPIENT_LABELS } from '../../../models/aiAnalysis/workflow'
import { isCombinedAction } from '../../../utils/aiAnalysis/draftPayload'
import { normalizeDraftText } from '../../../utils/aiAnalysis/normalizeDraftText'
import type { Action, ActionPayload } from '../../../models/aiAnalysis/workflow'
import type { AiDraftEditorProps, RenderEditor } from './types'
import styles from './styles.module.scss'

const { TextArea } = Input

const getRecipientType = (payload: ActionPayload): string => {
  if (payload.shared_with_agent) return RECIPIENT_LABELS.NOTARY
  if (payload.shared_with_client) return RECIPIENT_LABELS.CLIENT
  if (payload.shared_with_consumer) return RECIPIENT_LABELS.CONSUMER
  if (payload.shared_with_admin) return RECIPIENT_LABELS.SCHEDULER
  return RECIPIENT_LABELS.FALLBACK
}

export const getRecipientBucket = (action: Action): string => {
  if (action.action_type === ACTION_TYPES.COMBINED_TO_NOTARY) return RECIPIENT_LABELS.NOTARY
  if (action.action_type === ACTION_TYPES.COMBINED_TO_CLIENT) return RECIPIENT_LABELS.CLIENT
  return getRecipientType(action.action_payload)
}

const getRecipientLabel = (action: Action): string => {
  const bucket = getRecipientBucket(action)
  if (isCombinedAction(action)) {
    return `To ${bucket.toLowerCase()}`
  }

  const prefix = action.action_type === ACTION_TYPES.ACKNOWLEDGE ? 'Reply to' : 'Forward to'
  return `${prefix} ${bucket}`
}

const CHARACTER_LIMIT = 1500
const SHOW_COUNT_THRESHOLD = 1200

// N editors mount at once, and aria-describedby needs a mount-stable id per
// instance.
let editorInstanceCount = 0

const AiDraftEditor: React.FC<AiDraftEditorProps> = ({ action, onApprove, onDecline, currentText, onTextChange }) => {
  // Combined cards are the terminal, single response for their recipient — never
  // a "second message" continuing a prior forward, so they never get the reply styling.
  const isSecondMessage = action.action_type === ACTION_TYPES.ACKNOWLEDGE

  const recipientBucket = getRecipientBucket(action)
  const recipientLabel = getRecipientLabel(action)

  const [submittingAction, setSubmittingAction] = useState<'approve' | 'decline' | null>(null)
  const [charCountId] = useState<string>(() => {
    editorInstanceCount += 1
    return `ai-draft-char-count-${editorInstanceCount}`
  })

  const originalTextRef = useRef<string>(action.action_payload.text)
  const isEdited = normalizeDraftText(currentText) !== normalizeDraftText(originalTextRef.current)

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onTextChange(e.target.value)
  }

  const handleApprove = async () => {
    setSubmittingAction('approve')
    try {
      if (isEdited) {
        await onApprove(action.id, 'edited', { text: currentText })
      } else {
        await onApprove(action.id, 'approved', null)
      }
    } finally {
      setSubmittingAction(null)
    }
  }

  const handleDecline = async () => {
    setSubmittingAction('decline')
    try {
      await onDecline()
    } finally {
      setSubmittingAction(null)
    }
  }

  const isSubmitting = submittingAction !== null
  const isApproveDisabled = isSubmitting || !currentText.trim()
  const shouldShowCount = currentText.length > SHOW_COUNT_THRESHOLD

  return (
    <div className={styles.container} data-second-message={isSecondMessage}>
      <div className={styles.recipientInfo}>
        <SparkleIndicator aiGenerated={true} />
        <span className={styles.recipientText} data-test="recipient-label">
          {recipientLabel}
        </span>
      </div>

      <div className={styles.textareaWrapper}>
        {/* ConfigProvider overrides the default Ant Design theme tokens for this component only.
          We set borders to transparent and remove shadows so we can control them purely
          via CSS classes.
        */}
        <ConfigProvider
          theme={{
            components: {
              Input: {
                colorBorder: 'transparent',
                hoverBorderColor: 'transparent',
                activeBorderColor: 'transparent',
                activeShadow: 'none',
                boxShadow: 'none'
              }
            }
          }}
        >
          <TextArea
            className={classnames(styles.textarea, {
              [styles.textareaSecondMessage]: isSecondMessage
            })}
            value={currentText}
            onChange={handleTextChange}
            autoSize={{ minRows: 3, maxRows: 10 }}
            maxLength={CHARACTER_LIMIT}
            showCount={shouldShowCount}
            disabled={isSubmitting}
            aria-label="AI-generated comment draft"
            aria-describedby={charCountId}
          />
        </ConfigProvider>
        <span
          id={charCountId}
          style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}
        >
          Character limit: {CHARACTER_LIMIT}. Current count: {currentText.length}
        </span>
      </div>

      <div className={styles.actions}>
        <Button
          className={styles.cancelButton}
          onClick={handleDecline}
          disabled={isSubmitting}
          loading={submittingAction === 'decline'}
          aria-label={`Cancel AI-generated comment to ${recipientBucket}`}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          className={styles.createButton}
          onClick={handleApprove}
          disabled={isApproveDisabled}
          loading={submittingAction === 'approve'}
          aria-label={`Send comment to ${recipientBucket}`}
        >
          Send
        </Button>
      </div>
    </div>
  )
}

export const renderDefaultEditor: RenderEditor = (editorProps) => <AiDraftEditor {...editorProps} />

export default AiDraftEditor
